import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    createStyles,
    IconButton,
    makeStyles,
    Theme,
} from '@material-ui/core';
import i18nNamespaces from '../../../const/i18nNamespaces';
import {
    PrescriptionInPrescriptionSet,
    PrescriptionSetDto,
} from '../../../types/prescription';
import {TFunction, useTranslation} from 'react-i18next';
import {GetApp} from '@material-ui/icons';
import {Loading} from '../../index';
import {useState} from 'react';
import prescriptionsApi from '../../../api/prescriptions/prescriptionsApi';
import filesDownloadProvider from '../../../services/filesDownloadProvider';
import {getPrescriptionSetFileName} from '../../../utils/prescription';
import {getDosageDisplayValue} from '../../../utils/prescriptionDosage';

type PrescriptionsListItemProps = {
    prescriptionSet: PrescriptionSetDto;
    index: number;
    downloadable?: boolean;
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        accordionRoot: {
            '&.MuiAccordion-root': {
                padding: 0,
                border: 'none',
                boxShadow: 'none',
            },
            '&.MuiAccordionSummary-root': {
                padding: 0,
            },
            '&.MuiAccordion-root:before': {
                display: 'none',
            }
        },
        accordionSummaryRoot: {
            '&.MuiAccordionSummary-root': {
                padding: 0,
            }
        },
        prescriptionDetails: {
            display: 'flex',
            flexDirection: 'column',
        },
        prescriptionHeaderWrapper: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%'
        }
    }),
);

const PrescriptionSetHeader = ({
    prescriptionSet,
    index,
    t,
}: {
    prescriptionSet: PrescriptionSetDto;
    index: number;
    t: TFunction;
}) => (
    <Box>
        {index + 1}. {t('issueDate')}:{' '}
        {new Date(prescriptionSet.effectiveDateTime).toLocaleDateString('pl')}
        {', '}
        {t('positionsCount')}: {prescriptionSet.prescriptions.length}
        {', '}
        {t('accessCode')}: {prescriptionSet.code}
    </Box>
);

const PrescriptionDetails = ({
    prescription,
    index,
    t,
}: {
    prescription: PrescriptionInPrescriptionSet;
    index: number;
    t: TFunction;
}) => (
    <Box style={{marginLeft: 20}}>
        {prescription.order}
        {'. '}
        {prescription.medicinalProduct.displayValue}
        {', '}
        {prescription.quantity} {t('packs')}
        {', '}
        {prescription.medicationPack.displayValue}
        {', D.S. '}
        {getDosageDisplayValue(prescription, t)}
        {', '}
        {t('paymentRate')}
        {': '}
        {prescription.paymentLevelDisplayValue}
        {prescription.effectiveDate && (
            <>
                {', '}
                {t('effectiveDate')}:{' '}
                {new Date(prescription.effectiveDate).toLocaleDateString('pl')}
            </>
        )}
        
    </Box>
);
const PrescriptionsListItem = ({
    prescriptionSet,
    index,
    downloadable,
}: PrescriptionsListItemProps) => {
    const {t} = useTranslation(i18nNamespaces.PRESCRIPTIONS);
    const classes = useStyles();
    const [loading, setLoading] = useState<boolean>(false);
    const handleClickDownload = async (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        event.stopPropagation();
        try {
            setLoading(true);
            const {data: url} = await prescriptionsApi.getPrescriptionPdf(
                prescriptionSet.id,
            );
            filesDownloadProvider.downloadFileFromUrl(
                url,
                getPrescriptionSetFileName(prescriptionSet, t),
            );
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Loading withBackdrop loading={loading} />
            <Accordion classes={{root: classes.accordionRoot}}>
                <AccordionSummary style={{fontWeight: 'bold'}} classes={{root: classes.accordionSummaryRoot}}>
                    <Box className={classes.prescriptionHeaderWrapper}>
                        <PrescriptionSetHeader
                            prescriptionSet={prescriptionSet}
                            index={index}
                            t={t}
                        />
                        {downloadable && (
                            <IconButton
                                size={'small'}
                                style={{marginLeft: 10}}
                                onClick={handleClickDownload}
                            >
                                <GetApp fontSize={'small'} color={'secondary'}/>
                            </IconButton>
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Box className={classes.prescriptionDetails}>
                        {prescriptionSet.prescriptions.map(prescription => (
                            <PrescriptionDetails
                                key={index + prescription.order}
                                prescription={prescription}
                                index={index}
                                t={t}
                            />
                        ))}
                    </Box>
                </AccordionDetails>
            </Accordion>
        </>
    );
};

export default PrescriptionsListItem;
